import { useRef, type PointerEvent as ReactPointerEvent } from 'react';

import type { Block } from '../../domain/videoAnnotation';
import { msAusX, spurAnzahl, treffer, xAusMs, type Teil } from './leiste';
import styles from './Leisten.module.css';

/**
 * Die obere Leiste: je Spur eine Zeile, die Blöcke als Balken darin.
 *
 * **Drei Gesten, ein Zeiger:** in der Mitte anfassen verschiebt den ganzen Block, an den
 * Rändern zieht man seinen Anfang oder sein Ende. Welcher Teil gemeint ist, entscheidet
 * `treffer` — und dort steht auch, warum die Trefferzone breiter ist als der gezeichnete Rand.
 *
 * ⚠️ **Ein Tipp ins Leere legt keinen Block an.** Das tut die Bühne, wenn der Prüfer zu zeichnen
 * beginnt: ein Block ohne Marken ist nichts, was jemand behalten wollte (`leere-verwerfen` in
 * `reducer.ts`).
 */

/** Höhe einer Spurzeile. Muss mit `--spur-hoehe` in `Leisten.module.css` übereinstimmen. */
export const SPUR_HOEHE = 34;

interface Props {
  blocks: Block[];
  auswahl: string | null;
  durationMs: number;
  onWaehlen: (id: string | null) => void;
  /** Ein Rand wurde gezogen: der neue Zeitpunkt für diesen Rand. */
  onRand: (id: string, rand: 'anfang' | 'ende', ms: number) => void;
  /** Der Block wurde verschoben: um wie viele Millisekunden seit der letzten Meldung. */
  onVerschieben: (id: string, deltaMs: number) => void;
  onZugStart: () => void;
  onZugEnde: () => void;
}

/** Woran gerade gezogen wird. */
type Zug = { id: string; teil: Teil; letzteMs: number };

export function Blockleiste({
  blocks,
  auswahl,
  durationMs,
  onWaehlen,
  onRand,
  onVerschieben,
  onZugStart,
  onZugEnde,
}: Props) {
  const zug = useRef<Zug | null>(null);
  const spuren = spurAnzahl(blocks);

  const zuMs = (e: ReactPointerEvent<HTMLDivElement>): number => {
    const r = e.currentTarget.getBoundingClientRect();
    return msAusX(e.clientX - r.left, r.width, durationMs);
  };

  const down = (e: ReactPointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const getroffen = treffer(
      blocks,
      { x: e.clientX - r.left, y: e.clientY - r.top },
      { breite: r.width, durationMs, spurHoehe: SPUR_HOEHE },
    );

    if (!getroffen) {
      onWaehlen(null);
      return;
    }

    e.currentTarget.setPointerCapture(e.pointerId);
    onWaehlen(getroffen.id);
    // Der Verlaufseintrag entsteht **einmal** für den ganzen Zug; ohne das nähme ein Undo
    // einen einzigen Pixel zurück (s. `reducer.ts`).
    onZugStart();
    zug.current = { ...getroffen, letzteMs: zuMs(e) };
  };

  const move = (e: ReactPointerEvent<HTMLDivElement>) => {
    const laufend = zug.current;
    if (!laufend) return;

    const ms = zuMs(e);
    if (laufend.teil === 'mitte') {
      /*
       * ⚠️ **Gemeldet wird der Weg seit der letzten Meldung, nicht seit dem Anfassen.** Der
       * Reducer hält den Block an den Videorändern an; ein absoluter Versatz träfe nach dem
       * Anstossen auf einen Block, der nicht mehr dort liegt, wo die Rechnung ihn vermutet.
       */
      onVerschieben(laufend.id, ms - laufend.letzteMs);
    } else {
      onRand(laufend.id, laufend.teil, ms);
    }
    zug.current = { ...laufend, letzteMs: ms };
  };

  const up = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!zug.current) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    zug.current = null;
    onZugEnde();
  };

  return (
    <div
      className={styles.blockleiste}
      style={{ height: spuren * SPUR_HOEHE }}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
    >
      {blocks.map((b) => (
        <div
          key={b.id}
          className={b.id === auswahl ? styles.blockGewaehlt : styles.block}
          style={{
            left: xAusMs(b.fromMs, 100, durationMs) + '%',
            width: xAusMs(b.toMs - b.fromMs, 100, durationMs) + '%',
            top: b.track * SPUR_HOEHE,
          }}
        >
          <span className={styles.rand} aria-hidden="true" />
          <span className={styles.blockZahl}>{b.markers.length}</span>
          <span className={styles.rand} aria-hidden="true" />
        </div>
      ))}
    </div>
  );
}
